
import React from 'react';
import { Brain, Info } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ScienceInfo: React.FC = () => {
  return (
    <div className="glass rounded-2xl p-6 animate-fade-in">
      <div className="flex items-center gap-2 mb-4">
        <Brain size={18} className="text-primary" />
        <h3 className="text-lg font-medium">Scientific Information</h3>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Learn more about binaural beats and how different frequencies affect your brain state.
      </p>
      
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="delta">
          <AccordionTrigger className="text-sm font-medium">Delta Waves (0.5-4 Hz)</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Delta waves are the slowest recorded brain waves in humans and are most present during deep, dreamless sleep.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Associated with deepest meditation and restorative sleep</li>
              <li>Healing and regeneration are stimulated in this state</li>
              <li>Reduces cortisol and enhances DHEA for anti-aging effects</li>
              <li>Access to unconscious mind and collective unconscious</li>
              <li>Ideal for: Deep healing sleep, recovery from illness</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="theta">
          <AccordionTrigger className="text-sm font-medium">Theta Waves (4-8 Hz)</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Theta brainwaves occur most often in sleep but are also dominant in deep meditation and light sleep, including the REM dream state.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Gateway to learning, memory, and intuition</li>
              <li>Heightened receptivity and inspiration</li>
              <li>Vivid visualizations and increased creativity</li>
              <li>Deep emotional connections and insights</li>
              <li>Ideal for: Deep meditation, creative problem solving</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="alpha">
          <AccordionTrigger className="text-sm font-medium">Alpha Waves (8-13 Hz)</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Alpha brainwaves are dominant during quietly flowing thoughts and in some meditative states, representing the bridge between conscious thinking and subconscious mind.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Relaxed alertness, calm and centered</li>
              <li>Reduced anxiety and stress</li>
              <li>Positive thinking and emotional stability</li>
              <li>Enhanced learning and memory recall</li>
              <li>Ideal for: Relaxation, stress reduction, light meditation</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="beta">
          <AccordionTrigger className="text-sm font-medium">Beta Waves (13-30 Hz)</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Beta brainwaves dominate our normal waking state of consciousness when attention is directed towards cognitive tasks and the outside world.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Active, busy, or anxious thinking</li>
              <li>Focus, concentration and alertness</li>
              <li>Problem-solving and decision making</li>
              <li>Enhanced cognitive performance</li>
              <li>Ideal for: Work, study, analysis, active conversation</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="gamma">
          <AccordionTrigger className="text-sm font-medium">Gamma Waves (30-100 Hz)</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Gamma brainwaves are the fastest of brain waves and relate to simultaneous processing of information from different brain areas, associated with higher states of conscious perception.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Higher mental activity and perception</li>
              <li>Peak concentration and high-level information processing</li>
              <li>Associated with expanded consciousness states</li>
              <li>Present during bursts of insight and high-level cognitive functioning</li>
              <li>Ideal for: Advanced cognitive enhancement, high-level processing</li>
            </ul>
          </AccordionContent>
        </AccordionItem>
        
        <AccordionItem value="research">
          <AccordionTrigger className="text-sm font-medium">Research & Studies</AccordionTrigger>
          <AccordionContent className="text-sm">
            <p className="mb-2">
              Scientific research on binaural beats has shown promising results in several areas:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Memory Enhancement:</strong> Research published in the Journal of Cognitive Enhancement found theta binaural beats improved working memory performance.
              </li>
              <li>
                <strong>Anxiety Reduction:</strong> A 2019 study in the journal "Psychological Research" showed alpha binaural beats significantly reduced pre-operative anxiety.
              </li>
              <li>
                <strong>Sleep Quality:</strong> Research in "Frontiers in Human Neuroscience" found delta binaural beats improved deep sleep quality and increased sleep time.
              </li>
              <li>
                <strong>Attention:</strong> Beta frequency binaural beats were shown to enhance attention in a study published in "Psychological Research" (2017).
              </li>
            </ul>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
      
      <div className="mt-6 flex items-start gap-2 text-xs text-muted-foreground bg-primary/5 p-3 rounded-lg">
        <Info size={14} className="text-primary mt-0.5" />
        <p>
          Note: While binaural beats have shown promising results in research, individual experiences may vary. 
          Consult with a healthcare professional before using binaural beats if you have neurological conditions 
          or epilepsy.
        </p>
      </div>
    </div>
  );
};

export default ScienceInfo;
